import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useConvexAuth, useMutation } from 'convex/react';
import { MainRoutes } from '../../shared/models';
import { useLoadingWrapper } from '../../shared/hooks/useLoadingWrapper';
import { useContext, useEffect, useState } from 'react';
import { BaseContext } from '../../shared/base-context';
import { Id } from '../../convex/_generated/dataModel';
import { useStableQuery } from '../../shared/hooks/useStableQuery';
import { api } from '../../convex/_generated/api';
import { IntroSlidesViewer } from './components/IntroSlidesViewer';
import { OnboardingContent } from './components/OnboardingContent';
import { CheckinBanner } from './components/CheckinBanner';
import { XpProgressPanel } from './components/XpProgressPanel';
import { Button } from '../../catalyst-components/button';
import { ChatAboutButton } from './components/ChatAboutButton';
import { useLocalStorageImage } from '../../shared/hooks/useLocalStorageImage';
import { useConvexUser } from '../../shared/hooks/useConvexUser';
import { FAQHelpIcon } from '../../shared/components/FAQHelpIcon';

const isNewSession = (assignedAt?: string) => {
  if (!assignedAt) return false;
  const fourteenDays = 14 * 24 * 60 * 60 * 1000;
  return Date.now() - new Date(assignedAt).getTime() < fourteenDays;
};

export const Progress = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const { isAuthenticated } = useConvexAuth();
  const navigate = useNavigate();
  const { convexUserId, debug } = useContext(BaseContext);
  const onboardingCompleted = useStableQuery(api.user_metadata.getOnboardingCompleted, convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip");
  const introSlidesSeen = useStableQuery(api.user_metadata.getIntroSlidesSeen, convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip");
  const LoadingWrapper = useLoadingWrapper({ isLoading });
  const sessionInProgress = useStableQuery(api.user_progress.getCurrentSessionInProgress, convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip");
  const progressSummary = useStableQuery(api.user_progress.getProgressSummaryByUserId, convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip");
  const nextSessionsRecommendation = useStableQuery(api.user_progress.getNextSessionsRecommendation, convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip");
  const startSession = useMutation(api.sessions.startSession);
  const debugReasoning = useStableQuery(api.user_progress.getDebugReasoningByUserId, convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip");
  const completedSessions = useStableQuery(api.completed_sessions.listByUser, convexUserId ? { user_id: convexUserId as Id<"users">, limit: 1 } : "skip");
  const hasCompletedSessions = completedSessions && completedSessions.length > 0;
  const { user } = useConvexUser();
  const profileImage = useLocalStorageImage({
    url: user?.imageUrl || '',
    alt: 'Profile picture',
    className: "h-20 w-20 rounded-full border-primary-3 border-4"
  });

  if (!isAuthenticated) {
    navigate(MainRoutes.Landing);
  }

  useEffect(() => {
    if (onboardingCompleted !== undefined && introSlidesSeen !== undefined) {
      setIsLoading(false);
    }
  }, [onboardingCompleted, introSlidesSeen]);

  const handleContinueSession = () => {
    if (sessionInProgress) {
      navigate(`${MainRoutes.Chat}?sessionId=${sessionInProgress.session_id}`);
    }
  };

  const handleStartRecommendedSession = async (assistantId: Id<"assistants">) => {
    if (!convexUserId) return;
    
    try {
      // Start a new session with the recommended assistant
      const result = await startSession({
        user_id: convexUserId as Id<"users">,
        assistant_id: assistantId,
      });
      
      if (result?.session_id) {
        navigate(`${MainRoutes.Chat}?sessionId=${result.session_id}`);
      }
    } catch (error) {
    }
  };

  const renderRecommendation = () => {
    if (!nextSessionsRecommendation) return null;

    // Show loading message if generation is in progress
    if (nextSessionsRecommendation.is_generating) {
      return (
        <div className="w-full mb-8 border-2 border-border/50 bg-card p-6 rounded-xl shadow-sm animate-fade-in">
          <h3 className="text-foreground font-semibold text-lg mb-4">{t('progress.recommendationsTitle')}</h3>
          <div className="text-muted-foreground text-base leading-relaxed">
            <div className="flex items-center gap-3">
              <img src="/boar_track_v2_a_png.png" alt="Loading..." className="h-5 w-5" />
              <span>{t('progress.recommendationsLoading')}</span>
            </div>
          </div>
        </div>
      );
    }

    const { cta_sentence, assistant } = nextSessionsRecommendation;
    if (!cta_sentence) return null;

    return (
      <div className="w-full mb-8 border-2 border-border/50 bg-card p-6 rounded-xl shadow-sm">
        <h3 className="text-foreground font-semibold text-lg mb-4">{t('progress.recommendationsTitle')}</h3>
        <div className="text-muted-foreground text-base leading-relaxed whitespace-pre-wrap">
          {cta_sentence}
        </div>
        {assistant && (
          <div className="mt-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <Button
                color="primary"
                className="px-6 py-3 rounded-full"
                onClick={() => handleStartRecommendedSession(assistant.id)}
              >
                {assistant.display_name}
              </Button>
              {isNewSession(assistant.assigned_at) && (
                <span className="inline-block px-3 py-1 bg-accent text-accent-foreground rounded-full text-xs font-bold shadow-sm">
                  {t('common.new')}
                </span>
              )}
            </div>
          </div>
        )}
        {debug && debugReasoning && (
          <div className="text-foreground bg-secondary text-base leading-relaxed whitespace-pre-wrap mt-4 p-3 rounded-lg">
            {debugReasoning.debug_reasoning}
          </div>
        )}
      </div>
    );
  };

  return (
    <LoadingWrapper>
      <section className="min-h-screen bg-background pt-24">
        {/* Header Section */}
        {introSlidesSeen && onboardingCompleted && (
          <div className="px-6 pt-8 pb-8 text-center bg-gradient-to-r from-primary/5 to-accent/5 mb-5">
            <div className="max-w-2xl mx-auto animate-fade-in">
              <div className="mb-4 flex justify-center">{profileImage}</div>
              <h1 className="text-4xl font-semibold text-foreground mb-4">
                {progressSummary?.progress_summary ? t('progress.welcomeBackTitle') : t('progress.welcomeTitle')}
              </h1>
              {progressSummary?.progress_summary ? (
                <p className="text-xl text-muted-foreground">{t('progress.welcomeBackSubtitle')}</p>
              ) : (
                <div className="text-left text-base text-muted-foreground mt-2">
                  <p className="font-medium mb-2">{t('progress.welcomeSubtitle')}</p>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>
                      {t('progress.welcomeSubtitleItem1')}{' '}
                      <button
                        onClick={() => navigate(MainRoutes.BrowseAll)}
                        className="underline text-foreground hover:text-primary transition-colors"
                      >
                        {t('progress.welcomeSubtitleItem1Link')}
                      </button>
                    </li>
                    <li>
                      {t('progress.welcomeSubtitleItem2')}{' '}
                      <button
                        onClick={() => navigate(MainRoutes.Profile)}
                        className="underline text-foreground hover:text-primary transition-colors"
                      >
                        {t('progress.welcomeSubtitleItem2Link')}
                      </button>
                    </li>
                    <li>
                      {t('progress.welcomeSubtitleItem3')}{' '}
                      <button
                        onClick={() => navigate(MainRoutes.FAQ)}
                        className="underline text-foreground hover:text-primary transition-colors"
                      >{t('progress.welcomeSubtitleItem3Link')}
                      </button>
                    </li>
                  </ul>
                </div>
              )}
              <div className="mt-3">
                <FAQHelpIcon anchor="progress" size="md" tooltipText="What is My Progress?" />
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <section className="px-6 pb-24">
          <div className="max-w-2xl mx-auto space-y-8">
            {!introSlidesSeen && (
              <IntroSlidesViewer />
            )}
            {introSlidesSeen && !onboardingCompleted && (
              <OnboardingContent />
            )}
            {onboardingCompleted && (
              <div className="flex flex-col items-center justify-start w-full space-y-6">
                {/* Checkin Banner - shown prominently when user has a pending checkin (but not during active sessions) */}
                {!sessionInProgress && <CheckinBanner />}

                {/* Continue Session - most prominent */}
                {sessionInProgress && (
                  <div className="w-full border-2 border-primary/30 bg-card p-6 rounded-xl shadow-sm text-center">
                    <span className="text-foreground text-lg font-semibold block mb-2">{sessionInProgress.assistant_display_name}</span>
                    <span className="text-muted-foreground text-base block mb-4">{sessionInProgress.assistant_short_description}</span>
                    <Button
                      color="primary"
                      onClick={handleContinueSession}
                      className="px-8 py-3 rounded-full"
                    >
                      {t('common.continue')}
                    </Button>
                  </div>
                )}

                {/* Next Session Recommendation */}
                {!sessionInProgress && renderRecommendation()}

                {/* Browse All Sessions */}
                {!sessionInProgress && (
                  <div className="w-full text-center">
                    <p className="text-muted-foreground text-sm mb-4">
                      {t('progress.browseAllPrompt')}
                    </p>
                    <Button
                      color="secondary"
                      onClick={() => navigate(MainRoutes.BrowseAll)}
                      className="px-6 py-3 rounded-full"
                    >
                      {t('progress.browseAllButton')}
                    </Button>
                  </div>
                )}

                {/* XP Progress Panel - compact, below session suggestions */}
                <XpProgressPanel />

                {/* Progress Summary */}
                {progressSummary && progressSummary.progress_summary && (
                  <div className="w-full border-2 border-border/50 bg-card p-6 rounded-xl shadow-sm">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <h3 className="font-semibold text-foreground">{t('progress.journeyTitle')}</h3>
                      {hasCompletedSessions && (
                        <ChatAboutButton
                          contextType="progress_summary"
                          contextId="progress_summary"
                          systemMessage={`User tapped Discuss on their overall progress. Summary: "${progressSummary.progress_summary}". Respond in 1-2 sentences. Keep it brief.`}
                          autoSubmitPrompt="I'd like to talk about my overall progress and growth."
                          label="Discuss"
                          variant="text"
                        />
                      )}
                    </div>
                    <div className="text-muted-foreground text-base leading-relaxed whitespace-pre-wrap">
                      {progressSummary.progress_summary}
                    </div>
                  </div>
                )}

                {/* Link to Skills Page */}
                <div className="w-full text-center">
                  <Button
                    color="secondary"
                    onClick={() => navigate(MainRoutes.Skills)}
                    className="px-6 py-3 rounded-full"
                  >
                    {t('progress.skillsButton')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </section>
      </section>
    </LoadingWrapper>
  );
}; 